import {inject, Injectable} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {isEqual} from 'lodash';

export const ROWS_PER_PAGE_OPTIONS = [10, 20, 50];

export const PAGINATOR_Q_ARG_OFFSET = '_offset';
export const PAGINATOR_Q_ARG_LIMIT = '_limit';
export const PAGINATOR_Q_ARG_SORT = '_sort';
export const PAGINATOR_Q_ARG_ORDER = '_order';
export const PAGINATOR_Q_ARG_G_FILTER = '_gfilter';
export const PAGINATOR_Q_ARG_TO_SEARCH = '_search';
export const PAGINATOR_Q_ARG_FILTERS = '_filters';

export interface SDKQueryPaginatorConfig {
    offset?: number;
    limit?: number;
    sort?: string;
    order?: number;
    filter?: string;
    cols_to_search?: string[];
    filters_rows?: Record<string, string | number | boolean>;
}
export const DefaultSDKQueryPaginatorConfig: SDKQueryPaginatorConfig = {
    offset: 0,
    limit: 20,
    sort: 'created_at',
    order: -1,
    cols_to_search: [],
    filters_rows: {}
};

/**
 * Paginator service
 */
@Injectable({
    providedIn: 'root'
})
export class QueryPaginatorService {
    private route: ActivatedRoute = inject(ActivatedRoute);
    private router: Router = inject(Router);

    // ===============================================================
    // ===============================================================

    /**
     * Initialize the query paginator and try to restore parameters from URL
     * @param defaultConfig Default configuration
     * @param fromQueryParams Should gather parameters from URL?
     */
    public initRestorePaginator(
        defaultConfig: SDKQueryPaginatorConfig | undefined,
        fromQueryParams: boolean
    ): SDKQueryPaginatorConfig {
        const fromUrlConfig: SDKQueryPaginatorConfig = fromQueryParams
            ? QueryPaginatorService.fromQueryParams(this.route.snapshot.queryParams)
            : {};

        const config = QueryPaginatorService.mergeConfigs(DefaultSDKQueryPaginatorConfig, defaultConfig, fromUrlConfig);
        if (!config.limit || config.limit === -1) config.limit = DefaultSDKQueryPaginatorConfig.limit;

        this.updatePaginatorConfig(config, defaultConfig, false);

        return config;
    }
    /**
     * Update the paginator config
     * @param paginatorConfig New paginator config
     * @param updateUrl Should update URL?
     * @returns True if the configuration is different
     */
    public updatePaginatorConfig(
        paginatorConfig: SDKQueryPaginatorConfig,
        defaultConfig?: SDKQueryPaginatorConfig,
        updateUrl = true
    ): {updated: boolean; paginatorConfig: SDKQueryPaginatorConfig} {
        const mergedConfig = QueryPaginatorService.mergeConfigs(
            DefaultSDKQueryPaginatorConfig,
            defaultConfig,
            paginatorConfig
        );

        if (!isEqual(mergedConfig, paginatorConfig)) {
            if (updateUrl) {
                const currentQs = {...this.router.routerState.snapshot.root.queryParams};
                const excludeKeys = [
                    PAGINATOR_Q_ARG_OFFSET,
                    PAGINATOR_Q_ARG_LIMIT,
                    PAGINATOR_Q_ARG_SORT,
                    PAGINATOR_Q_ARG_ORDER,
                    PAGINATOR_Q_ARG_G_FILTER,
                    PAGINATOR_Q_ARG_TO_SEARCH,
                    PAGINATOR_Q_ARG_FILTERS
                ];
                for (const key of excludeKeys) {
                    delete currentQs[key];
                }

                if (!mergedConfig.limit || mergedConfig.limit === -1)
                    mergedConfig.limit = DefaultSDKQueryPaginatorConfig.limit;

                const newQs = QueryPaginatorService.toQueryParams(mergedConfig);
                const mergedQs = {...currentQs, ...newQs};

                this.router.navigate([], {
                    queryParams: mergedQs,
                    replaceUrl: true,
                    skipLocationChange: false
                });
            }

            return {updated: true, paginatorConfig: mergedConfig};
        }

        return {updated: false, paginatorConfig: mergedConfig};
    }

    // ===============================================================
    // ===============================================================

    /**
     * SDKQueryPaginatorConfig to query parameters
     * @param sdkQueryPaginatorConfig Source paginator config
     * @returns Built query parameters
     */
    public static toQueryParams(
        sdkQueryPaginatorConfig: SDKQueryPaginatorConfig | null
    ): Record<string, string | number | boolean> {
        const res: Record<string, string | number | boolean> = {};

        if (sdkQueryPaginatorConfig?.offset !== undefined) res[PAGINATOR_Q_ARG_OFFSET] = sdkQueryPaginatorConfig.offset;
        if (sdkQueryPaginatorConfig?.limit !== undefined) res[PAGINATOR_Q_ARG_LIMIT] = sdkQueryPaginatorConfig.limit;
        if (sdkQueryPaginatorConfig?.sort !== undefined) res[PAGINATOR_Q_ARG_SORT] = sdkQueryPaginatorConfig.sort;
        if (sdkQueryPaginatorConfig?.order !== undefined) res[PAGINATOR_Q_ARG_ORDER] = sdkQueryPaginatorConfig.order;
        if (sdkQueryPaginatorConfig?.filter !== undefined)
            res[PAGINATOR_Q_ARG_G_FILTER] = sdkQueryPaginatorConfig.filter;
        if (sdkQueryPaginatorConfig?.cols_to_search && sdkQueryPaginatorConfig?.cols_to_search.length > 0) {
            const cts = sdkQueryPaginatorConfig.cols_to_search.filter((x) => x).join(',') || '';
            if (cts.length > 0) res[PAGINATOR_Q_ARG_TO_SEARCH] = cts;
        }
        if (sdkQueryPaginatorConfig?.filters_rows) {
            const fr = Object.entries(sdkQueryPaginatorConfig.filters_rows)
                .filter((x) => x[1] !== null && x[1] !== undefined)
                .map((x) => `${x[0]}:${x[1]}`)
                .join(',');
            if (fr.length > 0) res[PAGINATOR_Q_ARG_FILTERS] = fr;
        }

        return res;
    }
    /**
     * Get a SDKQueryPaginatorConfig from query parameters
     * @param queryParams Source query parameters
     * @returns SDKQueryPaginatorConfig from query parameters
     */
    public static fromQueryParams(
        queryParams: Record<string, string | number | boolean | readonly (string | number | boolean)[]>
    ): SDKQueryPaginatorConfig {
        const res: SDKQueryPaginatorConfig = {};

        if (queryParams[PAGINATOR_Q_ARG_OFFSET]) {
            if (typeof queryParams[PAGINATOR_Q_ARG_OFFSET] === 'string')
                res.offset = parseInt(queryParams[PAGINATOR_Q_ARG_OFFSET], 10);
            else if (typeof queryParams[PAGINATOR_Q_ARG_OFFSET] === 'number')
                res.offset = queryParams[PAGINATOR_Q_ARG_OFFSET];
            else res.offset = DefaultSDKQueryPaginatorConfig.offset;
        }

        if (queryParams[PAGINATOR_Q_ARG_LIMIT]) {
            if (typeof queryParams[PAGINATOR_Q_ARG_LIMIT] === 'string')
                res.limit = parseInt(queryParams[PAGINATOR_Q_ARG_LIMIT], 10);
            else if (typeof queryParams[PAGINATOR_Q_ARG_LIMIT] === 'number')
                res.limit = queryParams[PAGINATOR_Q_ARG_LIMIT];
            else res.limit = DefaultSDKQueryPaginatorConfig.limit;
        }

        if (queryParams[PAGINATOR_Q_ARG_SORT]) {
            if (typeof queryParams[PAGINATOR_Q_ARG_SORT] === 'string') res.sort = queryParams[PAGINATOR_Q_ARG_SORT];
            else res.sort = DefaultSDKQueryPaginatorConfig.sort;
        }

        if (queryParams[PAGINATOR_Q_ARG_ORDER]) {
            if (typeof queryParams[PAGINATOR_Q_ARG_ORDER] === 'string')
                res.order = parseInt(queryParams[PAGINATOR_Q_ARG_ORDER], 10);
            else if (typeof queryParams[PAGINATOR_Q_ARG_ORDER] === 'number')
                res.order = queryParams[PAGINATOR_Q_ARG_ORDER];
            else res.order = DefaultSDKQueryPaginatorConfig.order;
        }

        if (queryParams[PAGINATOR_Q_ARG_G_FILTER]) {
            if (typeof queryParams[PAGINATOR_Q_ARG_G_FILTER] === 'string')
                res.filter = queryParams[PAGINATOR_Q_ARG_G_FILTER];
            else res.filter = DefaultSDKQueryPaginatorConfig.filter;
        }

        if (queryParams[PAGINATOR_Q_ARG_TO_SEARCH]) {
            if (typeof queryParams[PAGINATOR_Q_ARG_TO_SEARCH] === 'string')
                res.cols_to_search = queryParams[PAGINATOR_Q_ARG_TO_SEARCH].split(',');
            else res.cols_to_search = DefaultSDKQueryPaginatorConfig.cols_to_search;
        }

        if (queryParams[PAGINATOR_Q_ARG_FILTERS]) {
            if (typeof queryParams[PAGINATOR_Q_ARG_FILTERS] === 'string') {
                const rawPairs = queryParams[PAGINATOR_Q_ARG_FILTERS].split(',');
                rawPairs.forEach((x) => {
                    const parts = x.split(':');

                    if (parts.length !== 2 && parts[1].length != 0) return;

                    if (!res.filters_rows) res.filters_rows = {};

                    res.filters_rows[parts[0]] = parts[1];
                });
            } else res.filters_rows = DefaultSDKQueryPaginatorConfig.filters_rows;
        }

        return res;
    }

    // ===============================================================
    // ===============================================================

    /**
     * Merge multiple paginator config in one (From left to right)
     * @param configs Configurations to merge
     * @returns Merged configurations
     */
    private static mergeConfigs(...configs: (SDKQueryPaginatorConfig | undefined)[]): SDKQueryPaginatorConfig {
        const result: SDKQueryPaginatorConfig = {};

        for (const cfg of configs) {
            if (!cfg) continue;

            // primitive / number / boolean / string – simple overwrite
            const {offset, limit, sort, order, filter} = cfg;

            if (offset !== undefined) result.offset = offset;
            if (limit !== undefined) result.limit = limit;
            if (sort !== undefined) result.sort = sort;
            if (order !== undefined) result.order = order;
            if (filter !== undefined) result.filter = filter;

            if (Array.isArray(cfg.cols_to_search)) {
                // keep unique values – later arrays are appended
                const existing = new Set(result.cols_to_search ?? []);
                for (const v of cfg.cols_to_search) existing.add(v);
                result.cols_to_search = Array.from(existing);
            }

            if (cfg.filters_rows) {
                result.filters_rows = {
                    ...(result.filters_rows ?? {}),
                    ...cfg.filters_rows
                };
            }
        }

        return result;
    }
}
